import { _ as _ts_decorate } from "@swc/helpers/_/_ts_decorate";
import { _ as _ts_metadata } from "@swc/helpers/_/_ts_metadata";
import { computed, flow, observable } from "mobx";
import { routeLocationAffiliatePost, routeLocationAffiliateProductsGet, routeLocationEnvatoPurchasePost, routeLocationPaymentFailedPaymentDetailsGet, routeLocationSupportSubscriptionCancelationPost, routeLocationUserDoubleOptInPatch, routeLocationUserEmailActivatePatch, routeLocationUserSessionMagicLinkPost, routeLocationUserSessionPost } from "@devowl-wp/api-real-commerce";
import { BaseOptions } from "@devowl-wp/utils";
import { RealCommerceError } from "../exceptions/realCommerceError.js";
import { Session } from "../models/accountV1/session.js";
import { PaddleSubscription } from "../models/paddleSubscription.js";
import { __ } from "../utils/i18n.js";
import { realCommerceRequest } from "../utils/realCommerceRequest.js";
class AccountV1Store extends BaseOptions {
    get isLoggedIn() {
        return !!this.session?.user;
    }
    /**
     * Is the current logged in user already an active affiliate?
     */ get isAffiliate() {
        return this.isLoggedIn && !!this.session.affiliate;
    }
    constructor(rootStore){
        super(), this.busyAffiliateProducts = false, this.affiliateProducts = [], this.createSessionFromJwt = flow(function*(jwt) {
            const session = new Session(jwt, this);
            yield session.probablyRenewJwt();
            yield session.fetchUser();
            this.session = session;
        }), this.createAccountEnvato = flow(function*(data) {
            const { session } = this;
            if (session) {
                yield session.probablyRenewJwt();
            }
            try {
                return yield realCommerceRequest({
                    location: routeLocationEnvatoPurchasePost,
                    request: {
                        envatoPurchase: {
                            ...data,
                            isTermsAccepted: true
                        }
                    },
                    settings: session ? {
                        headers: session.authorizationHeader
                    } : {}
                });
            } catch (e) {
                throw new RealCommerceError(e);
            }
        }), this.createAffiliate = flow(function*(affiliate, user, inviteUuid) {
            const { session } = this;
            if (session) {
                yield session.probablyRenewJwt();
            }
            try {
                const result = yield realCommerceRequest({
                    location: routeLocationAffiliatePost,
                    request: {
                        affiliate,
                        affiliateInvite: inviteUuid ? {
                            uuid: inviteUuid
                        } : undefined,
                        user: session ? undefined : user
                    },
                    multipart: true,
                    settings: session ? {
                        headers: session.authorizationHeader
                    } : {}
                });
                if (session) {
                    // Does not work as `commissions` is missing in response of `POST` request
                    //runInAction(() => {
                    //    session.affiliate = new Affiliate(result, session);
                    //});
                    try {
                        yield session.fetchAffiliate();
                    } catch (e) {
                    // Ignore errors as it gets handled via `affiliateError` (e.g. pending status)
                    }
                }
                return result;
            } catch (e) {
                throw new RealCommerceError(e);
            }
        }), this.activateUser = flow(function*(confirmationCode) {
            try {
                return yield realCommerceRequest({
                    location: routeLocationUserDoubleOptInPatch,
                    request: {
                        user: {
                            confirmationCode
                        }
                    }
                });
            } catch (e) {
                throw new RealCommerceError(e);
            }
        }), this.fetchPaymentFailedDetails = flow(function*(subscriptionUuid) {
            try {
                const { subscription } = yield realCommerceRequest({
                    location: routeLocationPaymentFailedPaymentDetailsGet,
                    params: {
                        subscriptionUuid
                    }
                });
                // TODO: differ between ecommerce systems
                this.expiredPaymentDetailsSubscription = new PaddleSubscription(subscription, this.session);
            } catch (e) {
                throw new RealCommerceError(e);
            }
        }), this.fetchAffiliateProducts = flow(function*() {
            this.busyAffiliateProducts = false;
            try {
                const { products } = yield realCommerceRequest({
                    location: routeLocationAffiliateProductsGet
                });
                this.affiliateProducts = products;
            } catch (e) {
                throw new RealCommerceError(e);
            } finally{
                this.busyAffiliateProducts = false;
            }
        }), this.requestCancellation = flow(function*(subscriptionCancelationSupportTicket) {
            try {
                yield realCommerceRequest({
                    location: routeLocationSupportSubscriptionCancelationPost,
                    request: {
                        subscriptionCancelationSupportTicket
                    }
                });
            } catch (e) {
                throw new RealCommerceError(e);
            }
        }), this.sendMagicLink = flow(function*(email) {
            try {
                yield realCommerceRequest({
                    location: routeLocationUserSessionMagicLinkPost,
                    request: {
                        user: {
                            email
                        }
                    }
                });
            } catch (e) {
                throw new RealCommerceError(e);
            }
        }), this.updateEmailAddress = flow(function*(newEmailConfirmationCode) {
            try {
                yield realCommerceRequest({
                    location: routeLocationUserEmailActivatePatch,
                    request: {
                        user: {
                            newEmailConfirmationCode
                        }
                    }
                });
                this.session?.fetchUser();
            } catch (e) {
                throw new RealCommerceError(e);
            }
        }), this.receiveJwt = flow(function*(magicLinkToken) {
            try {
                const response = yield realCommerceRequest({
                    location: routeLocationUserSessionPost,
                    request: {
                        userSession: {
                            magicLinkToken
                        }
                    }
                });
                return response.jwt;
            } catch (e) {
                throw new RealCommerceError(e);
            }
        });
        this.rootStore = rootStore;
    }
    // @TODO: dynamically get from REST API and move to product model
    get affiliateManufacturer() {
        return {
            name: "devowl.io GmbH",
            link: __("https://devowl.io/"),
            terms: __("https://devowl.io/affiliate-terms/"),
            privacy: __("https://devowl.io/privacy-policy/")
        };
    }
}
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof Session === "undefined" ? Object : Session)
], AccountV1Store.prototype, "session", void 0);
_ts_decorate([
    observable
], AccountV1Store.prototype, "busyAffiliateProducts", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", Object)
], AccountV1Store.prototype, "affiliateProducts", void 0);
_ts_decorate([
    observable,
    _ts_metadata("design:type", typeof Subscription === "undefined" ? Object : Subscription)
], AccountV1Store.prototype, "expiredPaymentDetailsSubscription", void 0);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], AccountV1Store.prototype, "isLoggedIn", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], AccountV1Store.prototype, "isAffiliate", null);
export { AccountV1Store };
